import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function CheckoutSuccess() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = useState("pending"); // pending | paid - expired - stuck
  const [attempts, setAttempts] = useState(0);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    setStatus("pending");
    setAttempts(0);
    const poll = async (n = 0) => {
      if (cancelled) return;
      if (n >= 8) { setStatus("stuck"); return; }
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        setAttempts(n + 1);
        if (r.data.payment_status === "paid") { setStatus("paid"); return; }
        if (r.data.status === "expired") { setStatus("expired"); return; }
        setTimeout(() => poll(n + 1), 2000);
      } catch {
        setTimeout(() => poll(n + 1), 2000);
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId, retry]);

  return (
    <div className="min-h-screen grid place-items-center p-6">
      <Card className="p-10 max-w-md w-full text-center" data-testid="checkout-success-card">
        {status === "paid" ? (
          <>
            <CheckCircle2 className="w-12 h-12 text-primary mx-auto mb-4" />
            <h1 className="font-display text-4xl mb-2">Order received</h1>
            <p className="text-muted-foreground mb-6">Your kitchen ticket just printed.</p>
          </>
        ) : status === "expired" ? (
          <>
            <h1 className="font-display text-3xl mb-2">Session expired</h1>
            <p className="text-muted-foreground mb-6">Please try again.</p>
          </>
        ) : status === "stuck" ? (
          <>
            <h1 className="font-display text-3xl mb-2">Still confirming…</h1>
            <p className="text-sm text-muted-foreground mb-6">
              We couldn't confirm your payment after several attempts. If you were
              charged, your order is safe and we'll email you shortly. Otherwise,
              please try again or contact support.
            </p>
            <div className="flex flex-col gap-2">
              <Button onClick={() => setRetry(r => r + 1)} variant="outline" className="w-full">
                Check again
              </Button>
            </div>
          </>
        ) : (
          <>
            <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
            <h1 className="font-display text-3xl mb-2">Confirming payment…</h1>
            <p className="text-sm text-muted-foreground mb-6">Attempt {attempts}/8</p>
          </>
        )}
        {status !== "stuck" && (
          <Link to={`/t/${slug}`}><Button variant="outline" className="w-full">Back to menu</Button></Link>
        )}
      </Card>
    </div>
  );
}
